import {
  MissionCategoryEnum,
  MissionNameEnum,
} from '../../types/enums/missionName.enum';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import cx from 'classnames';

import { useAirdropMissions } from './useAirdropMissions';
import {
  MissionTitleByEnumValueUpdated,
  QuestTitleByEnumValueUpdated,
} from './utils';
import { NewMissionCard } from './MissionCard/NewMissionCard';
import { Grid } from '@mui/material';
import styles from '../../Pages/Airdrop/AirdropPage.module.css';
import classNames from 'classnames';
import { QuestType } from '../../types/quest.type';
import { useUserContext } from '../../context/user.context';
import { isDisabled } from '../../utils/constants';
import { AirdropWrapper } from '../../Pages/Airdrop/AirdropWrapper';
import { useAirdrop } from '../../Pages/Airdrop/useAirdrop';

const TabItemsEnum = {
  MULTIPLIERS: 'Multipliers',
  QUESTS: 'Quests',
  INVITES: 'Invites',
};

const DisabledTabItems = [TabItemsEnum.INVITES];

type AirdropMissionsProps = {
  referralCode: string;
  setIsRedeemModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AirdropMissions = ({
  referralCode,
  setIsRedeemModalOpen,
}: AirdropMissionsProps) => {
  const { isLoggedIn } = useUserContext();
  const { isAirdropPaused } = useAirdrop();
  const {
    multipliers,
    quests,
    twitter: { twitterLogin },
    telegram: { telegramLogin },
  } = useAirdropMissions();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(TabItemsEnum.MULTIPLIERS);

  const copyToClipboard = useCallback(async () => {
    if (referralCode) {
      await navigator.clipboard.writeText(referralCode.toString());
      toast.success('Code Copied Successfully!');
    } else {
      toast.error('No code detected');
    }
  }, [referralCode]);

  const Tabs = () => {
    return (
      <Grid
        container
        display="flex"
        gap="20px"
        className={styles.tabsContainer}
      >
        {Object.values(TabItemsEnum).map((item, index) => (
          <Grid
            item
            key={index}
            className={classNames(
              styles.tabItem,
              activeTab === item && styles.active,
            )}
            display="flex"
            justifyContent="center"
            onClick={() => {
              if (DisabledTabItems.includes(item)) {
                const section = document.getElementById(
                  'referralLeaderboardId',
                );
                if (section) {
                  section.scrollIntoView({ behavior: 'smooth' });
                }
                return;
              }
              setActiveTab(item);
            }}
          >
            {item}
          </Grid>
        ))}
      </Grid>
    );
  };

  const Multipliers = () => {
    if (!multipliers.length) {
      return null;
    }

    const multipliersOrder = Object.keys(MissionTitleByEnumValueUpdated);

    const sortedMultipliers = multipliers.sort((a, b) => {
      return (
        multipliersOrder.indexOf(a.name) - multipliersOrder.indexOf(b.name)
      );
    });

    const totalMultipliers = 15;

    const HardcodedMultipliers = new Array(
      totalMultipliers - multipliers?.length,
    )
      .fill(null)
      .map((_, idx) => (
        <NewMissionCard
          comingSoon
          key={`hardcoded-mission-${idx}`}
          text=""
          header={
            <>
              <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
                Coming soon...
              </div>
            </>
          }
        />
      ));

    const RenderMultiplier = (multiplier, index) => {
      const baseProps = {
        buttonText: multiplier.completed
          ? 'Applied'
          : MissionTitleByEnumValueUpdated[multiplier.name]?.buttonText ||
            'Bridge',
        completed: multiplier.completed,
        key: `mission-card-${index}`,
        title: MissionTitleByEnumValueUpdated[multiplier.name]?.title,
        text: MissionTitleByEnumValueUpdated[multiplier.name]?.text,
        isExpandable: Boolean(
          MissionTitleByEnumValueUpdated[multiplier.name]?.text,
        ),
        header: (
          <>
            <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
              {multiplier.multiplier}x
            </div>
            <div className="text-white text-lg md:text-[30px] font-bold leading-[22px] md:leading-9">
              MULTIPLIER
            </div>
          </>
        ),
      };

      switch (multiplier.name) {
        case MissionNameEnum.TELEGRAM_JOIN: {
          return (
            <NewMissionCard
              {...baseProps}
              isExpandable
              buttonText={multiplier.completed ? 'Applied' : 'Join'}
              text={
                <div className="uppercase">
                  by clicking{' '}
                  <span
                    className="text-orange64 cursor-pointer"
                    onClick={
                      isLoggedIn && !baseProps.completed && !isDisabled
                        ? () => {
                            void telegramLogin();
                          }
                        : undefined
                    }
                  >
                    here
                  </span>
                </div>
              }
              onClick={
                isLoggedIn && !baseProps.completed && !isDisabled
                  ? () => {
                      void telegramLogin();
                    }
                  : undefined
              }
            />
          );
        }
        case MissionNameEnum.TWITTER_FOLLOW: {
          return (
            <NewMissionCard
              {...baseProps}
              isExpandable
              buttonText={multiplier.completed ? 'Applied' : 'Follow'}
              text={
                <div className="uppercase">
                  by clicking{' '}
                  <span
                    className="text-orange64 cursor-pointer"
                    onClick={
                      isLoggedIn && !baseProps.completed && !isDisabled
                        ? () => {
                            void twitterLogin();
                          }
                        : undefined
                    }
                  >
                    here
                  </span>
                </div>
              }
              onClick={
                isLoggedIn && !baseProps.completed && !isDisabled
                  ? () => {
                      void twitterLogin();
                    }
                  : undefined
              }
            />
          );
        }
        case MissionNameEnum.WEEKLY_MULTIPLIER: {
          return (
            <NewMissionCard
              {...baseProps}
              header={
                <>
                  <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
                    {multiplier.multiplier}x
                  </div>
                  <div className="text-white text-lg md:text-[30px] font-bold leading-[22px] md:leading-9 uppercase">
                    per Week
                  </div>
                </>
              }
              onClick={() => !isDisabled && navigate('/bridge')}
            ></NewMissionCard>
          );
        }
        case MissionNameEnum.EACH_REFERRAL_DEPOSIT_MORE_THAN_0_POINT_1_ETH: {
          return (
            <NewMissionCard
              {...baseProps}
              header={
                <>
                  <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
                    {multiplier.multiplier}x
                  </div>
                  <div className="text-white text-lg md:text-[30px] font-bold leading-[22px] md:leading-9 uppercase">
                    per Referral
                  </div>
                </>
              }
              onClick={!isDisabled && copyToClipboard}
            ></NewMissionCard>
          );
        }
        case MissionNameEnum.MAINTAIN_MORE_THAN_5_ETH_TO_TGE: {
          return (
            <NewMissionCard
              {...baseProps}
              header={
                <>
                  <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
                    {multiplier.multiplier}x
                  </div>
                  <div className="text-white text-lg md:text-[30px] font-bold leading-[22px] md:leading-9">
                    PER 1 ozETH ABOVE 5 ozETH
                  </div>
                </>
              }
              onClick={() => !isDisabled && navigate('/bridge')}
            ></NewMissionCard>
          );
        }
        case MissionNameEnum.MAINTAIN_MORE_THAN_15000_USDT_TO_TGE: {
          return (
            <NewMissionCard
              {...baseProps}
              header={
                <>
                  <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
                    {multiplier.multiplier}x
                  </div>
                  <div className="text-white text-lg md:text-[30px] font-bold leading-[22px] md:leading-9">
                    PER 3,000 ozUSD ABOVE 15,000 ozUSD
                  </div>
                </>
              }
              onClick={() => !isDisabled && navigate('/bridge')}
            ></NewMissionCard>
          );
        }
        default: {
          return (
            <NewMissionCard
              {...baseProps}
              onClick={
                baseProps.buttonText === 'Add code'
                  ? () => !isDisabled && setIsRedeemModalOpen(true)
                  : () => !isDisabled && navigate('/bridge')
              }
            />
          );
        }
      }
    };

    return (
      <Grid
        container
        display="flex"
        gap={{ xs: 8, md: 16 }}
        justifyContent={{ xs: 'space-around', lg: 'flex-start' }}
      >
        <Grid
          container
          display="flex"
          gap={4}
          justifyContent={'center'}
          className="mt-16 md:mt-8"
        >
          <div
            className={cx([
              'missionsCategory',
              'flex w-full py-2 justify-center items-center gap-2 self-stretch rounded-[50px_50px_4px_4px]',
            ])}
          >
            <div className="missionsCategoryTitle">General Missions</div>
          </div>
          <Grid
            container
            display="flex"
            gap={{ xs: 2, md: 7 }}
            justifyContent={{ xs: 'center', md: 'space-between' }}
          >
            {sortedMultipliers
              ?.filter(
                (multiplier) =>
                  multiplier?.category === MissionCategoryEnum.GENERAL,
              )
              ?.map(RenderMultiplier)}
          </Grid>
        </Grid>
        <Grid container display="flex" gap={4} justifyContent={'center'}>
          <div
            className={cx([
              'missionsCategory',
              'flex w-full py-2 justify-center items-center gap-2 self-stretch rounded-[50px_50px_4px_4px]',
            ])}
          >
            <div className="missionsCategoryTitle">ETH Staking Missions</div>
          </div>

          <Grid
            container
            display="flex"
            gap={{ xs: 2, md: 7 }}
            justifyContent={{ xs: 'center', lg: 'space-between' }}
          >
            {sortedMultipliers
              ?.filter(
                (multiplier) =>
                  multiplier?.category === MissionCategoryEnum.ETH,
              )
              ?.map(RenderMultiplier)
              .concat(HardcodedMultipliers)}
          </Grid>
        </Grid>
        <Grid container display="flex" gap={4} justifyContent={'center'}>
          <div
            className={cx([
              'missionsCategory',
              'flex w-full py-2 justify-center items-center gap-2 self-stretch rounded-[50px_50px_4px_4px]',
            ])}
          >
            <div className="missionsCategoryTitle">
              Stablecoin Staking Missions
            </div>
          </div>

          <Grid
            container
            display="flex"
            gap={{ xs: 2, md: 7 }}
            justifyContent={{ xs: 'center', lg: 'space-between' }}
          >
            {sortedMultipliers
              ?.filter(
                (multiplier) =>
                  multiplier?.category === MissionCategoryEnum.STABLECOIN,
              )
              ?.map(RenderMultiplier)
              .concat(HardcodedMultipliers)}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const Quests = () => {
    if (!quests.length) {
      return null;
    }

    const questsOrder = Object.keys(QuestTitleByEnumValueUpdated);

    const sortedQuests = quests.sort((a, b) => {
      return questsOrder.indexOf(a.name) - questsOrder.indexOf(b.name);
    });

    const RenderQuest = (quest: QuestType, index: number) => {
      const locked =
        (index && sortedQuests.slice(0, index).some((q) => !q.completed)) ||
        false;

      const baseProps = {
        locked,
        completed: quest.completed,
        buttonText: locked ? 'Locked' : quest.completed ? 'Achieved' : 'Open',
        key: `quest-card-${index}`,
        title: locked ? (
          <div className="text-white text-center font-poppins md:text-xl text-xs md:leading-[25px] leading-[15px] font-normal leading-normal uppercase">
            Complete{' '}
            <span className="text-orange64 font-bold uppercase">{`Quest ${index}`}</span>{' '}
          </div>
        ) : (
          <div className="text-white text-center font-poppins md:text-xl text-xs md:leading-[25px] leading-[15px] font-normal ">
            {QuestTitleByEnumValueUpdated[quest.name]?.title}
          </div>
        ),
        header: locked ? (
          <></>
        ) : (
          <>
            <div className="font-bold text-orange64 text-lg md:text-4xl leading-[22px] md:leading-[36px]">
              {quest.points}
            </div>
            <div className="text-white text-lg md:text-[30px] font-bold leading-[22px] md:leading-9 font-bold uppercase">
              Points
            </div>
          </>
        ),
        text: QuestTitleByEnumValueUpdated[quest.name]?.text,
        isExpandable:
          !locked && Boolean(QuestTitleByEnumValueUpdated[quest.name]?.text),
      };

      return <NewMissionCard {...baseProps} />;
    };

    return (
      <Grid
        container
        display="flex"
        gap={{ xs: 2, md: 7 }}
        justifyContent={{ xs: 'center', md: 'space-between' }}
      >
        {sortedQuests?.map(RenderQuest)}
      </Grid>
    );
  };

  return (
    <Grid container className="gap-5 md:px-0 px-4 relative">
      <Tabs />
      {activeTab === TabItemsEnum.MULTIPLIERS && <Multipliers />}
      {activeTab === TabItemsEnum.QUESTS && <Quests />}
      <Grid container display="flex" justifyContent={'center'} className="mt-8">
        <p className="referText text-center">
          Refer friends, get 15% of their Airdrop Points!
        </p>
      </Grid>
      {isAirdropPaused && (
        <Grid className="airdropWrapper">
          <AirdropWrapper />
        </Grid>
      )}
    </Grid>
  );
};
